import { clsx, type ClassValue } from 'clsx'
import { Loader2, type PhosphorIcon } from '../icons'
import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react'

export const cn = (...v: ClassValue[]) => clsx(v)

/* ------------------------------------------------------------------ Icons */

/** Single icon contract: every icon renders through Phosphor (regular weight for UI, duotone for navigation sections). */
export type IconComponent = PhosphorIcon

export function Icon({ icon: I, size = 16, className, weight = 'regular' }: { icon: IconComponent; size?: number; className?: string; weight?: 'regular' | 'duotone' | 'fill' | 'bold' }) {
  return <I size={size} weight={weight} className={cn('shrink-0', className)} aria-hidden />
}

/* ---------------------------------------------------------------- Button */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle' | 'link'
type Size = 'xs' | 'sm' | 'md' | 'lg'

const variants: Record<Variant, string> = {
  primary: 'btn-primary btn-sheen bg-brand text-white hover:bg-brand-hover shadow-xs disabled:opacity-60',
  secondary: 'bg-surface text-fg border border-border hover:bg-surface-hover hover:border-border-strong shadow-xs disabled:opacity-60',
  ghost: 'text-fg-muted hover:text-fg hover:bg-surface-hover disabled:opacity-50',
  subtle: 'bg-neutral-soft text-fg hover:bg-surface-active disabled:opacity-60',
  danger: 'bg-danger text-white hover:opacity-90 shadow-xs disabled:opacity-60',
  link: 'text-brand-text hover:underline underline-offset-2 px-0 h-auto',
}
const sizes: Record<Size, string> = {
  // the portal's controls (owner, 2026-10-21: «تشيل الثيم القديم»): one 10px radius, buttons as tall as the inputs
  xs: 'h-7 px-2 text-xs gap-1 rounded-lg',
  sm: 'h-8 px-3 text-sm gap-1.5 rounded-[9px]',
  md: 'h-10 px-4 text-sm gap-2 rounded-[10px]',
  lg: 'h-11 px-5 text-base gap-2 rounded-xl',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  icon?: IconComponent
  iconEnd?: IconComponent
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, iconEnd, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  const iconSize = size === 'lg' ? 18 : size === 'xs' ? 14 : 16
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap font-medium transition-[background,border,color,box-shadow,transform] duration-150 enabled:active:scale-[0.97] disabled:cursor-not-allowed',
        variants[variant],
        variant !== 'link' && sizes[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 size={iconSize} className="animate-spin" aria-hidden /> : icon ? <Icon icon={icon} size={iconSize} /> : null}
      {children}
      {iconEnd && !loading ? <Icon icon={iconEnd} size={iconSize} /> : null}
    </button>
  )
})

export const IconButton = forwardRef<HTMLButtonElement, Omit<ButtonProps, 'children' | 'iconEnd'> & { label: string; icon: IconComponent }>(
  function IconButton({ label, icon, size = 'md', variant = 'ghost', className, ...rest }, ref) {
    const box = { xs: 'size-7', sm: 'size-8', md: 'size-10', lg: 'size-11' }[size]
    return <Button ref={ref} aria-label={label} title={label} variant={variant} size={size} icon={icon} className={cn(box, '!px-0', className)} {...rest} />
  },
)

/* ----------------------------------------------------------------- Badge */

export type Tone = 'neutral' | 'brand' | 'accent' | 'info' | 'success' | 'warning' | 'danger'
const tones: Record<Tone, string> = {
  neutral: 'bg-neutral-soft text-fg-muted',
  brand: 'bg-brand-soft text-brand-text',
  accent: 'bg-accent-soft text-accent',
  info: 'bg-info-soft text-info',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
}

export function Badge({ tone = 'neutral', dot, className, children, ...rest }: HTMLAttributes<HTMLSpanElement> & { tone?: Tone; dot?: boolean }) {
  return (
    <span className={cn('inline-flex min-h-[22px] max-w-full items-center gap-1 truncate rounded-full px-2.5 py-0.5 text-xs font-semibold leading-snug', tones[tone], className)} {...rest}>
      {dot && <span className="size-1.5 shrink-0 rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  )
}

/** Numeric counter used in navigation and tabs. */
export function CountBadge({ value, tone = 'accent', max = 999 }: { value: number | undefined; tone?: Tone; max?: number }) {
  if (!value) return null
  return <span className={cn('tabular inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-2xs font-semibold', tones[tone])}>{value > max ? `${max}+` : value}</span>
}

/* ------------------------------------------------------------------ Card */

export function Card({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('card-polish rounded-[18px] border border-border/70 bg-surface shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-16px_rgba(16,24,40,0.16)]', className)} {...rest} />
}

export function CardHeader({ title, description, actions, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-start justify-between gap-3 border-b border-border/60 px-5 py-4', className)}>
      <div className="min-w-0">
        <h3 className="truncate text-base font-bold text-fg-strong">{title}</h3>
        {description && <p className="mt-0.5 text-xs text-fg-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

export function CardBody({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-5', className)} {...rest} />
}

/* -------------------------------------------------------------- Skeleton */

export function Skeleton({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('skeleton rounded-md', className)} aria-hidden {...rest} />
}

export function Spinner({ size = 16, className }: { size?: number; className?: string }) {
  return <Loader2 size={size} className={cn('animate-spin text-fg-subtle', className)} aria-label="Loading" />
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-surface px-1 font-mono text-2xs text-fg-muted">{children}</kbd>
}

export function Divider({ className }: { className?: string }) {
  return <hr className={cn('border-border', className)} />
}
